import Reflux from 'reflux-react-16'
import moment from 'moment'
import _ from 'lodash'

import dbCodes from '../../server/dbCodes.js'

import socket from '../socketio/socketio.js'
import OrderActions from '../actions/OrderActions.js'
import LoginActions from '../actions/LoginActions.js'
import geolocation from '../utils/geolocation.js'
import time from 'server/utils/time.js'
import {emit} from 'messenger/socketio/socketEmit.js'
import stopBundles from '../../utils/stopBundles.js'
import {isRetourOrder} from '../../utils/retourAddresses.js'

const LOCATION_UPDATE_INTERVAL_MS = moment.duration(1, 'minute').asMilliseconds()

// LEGACY: remove once no messenger opens a route from before #3671 anymore (snapshots without colli and status).
// The completed colli of an address, read from the order's current state
function legacyCompletedColli(order, address) {
    return order.colli.filter((collo) => {
        if (address.type === 'pickup' && collo.pickupAddressIndex === address.index) {
            return ['notcollected', 'collected', 'athub', 'outfordelivery', 'delivered', 'notdelivered', 'missing', 'refused', 'manco', 'retour'].includes(collo.status)
        }

        if (address.type === 'delivery' && collo.deliveryAddressIndex === address.index) {
            return ['notcollected', 'notsupplied', 'delivered', 'notdelivered', 'missing', 'refused', 'manco', 'retour'].includes(collo.status)
        }

        return false
    })
}

export default class OrderStore extends Reflux.Store {
    constructor() {
        super()

        this.state = {
            date: moment().format('YYYY-MM-DD'),
            customers: [],
            routes: {},
            shifts: [],
            shift: undefined, // is initially undefined to be able to distinguish between not loaded (undefined) and no active shift (null)
            selectedShift: localStorage.selectedShift || '',
            selectedRoute: localStorage.selectedRoute || '',
            route: {},
            isScanning: false,
            readyColli: {},
            routeUpdatedNotifcation: {added: [], removed: [], addedMessage: '', removedMessage: ''},
            loading: false,
            routeLoading: false,
            routeView: 'list',
            location: null
        }

        this.listenables = OrderActions

        // This store is a singleton, so its constructor only naturally runs once - the first time any component
        // references it. If that happens before the user is logged in (e.g. landing on /koerier while logged
        // out, then getting redirected to /koerier/login), mtoken isn't set yet and this data never loads, even
        // after the user then logs in and gets routed back - nothing else re-triggers it. LoginActions.getAccount
        // fires both on a normal load with an existing token and right after a fresh login (see
        // LoginStore.onLogin), so listening for it here closes that gap. Guarded to run at most once per page
        // load: this is a one-time catch-up fetch for the auth-timing edge case above, not a general refresh
        // mechanism (that's handled separately by the updateOrder/updateShifts/updateCustomers socket listeners
        // below), so it must not re-run every time getAccount is called.
        let hasLoadedInitialData = false

        const loadInitialData = () => {
            if (hasLoadedInitialData || !localStorage.mtoken) return
            hasLoadedInitialData = true

            OrderActions.getRoutes(true)
            OrderActions.getCustomers()
            OrderActions.getShifts()
        }

        loadInitialData()
        LoginActions.getAccount.listen(loadInitialData)

        socket.on('updateOrder', () => {
            OrderActions.getRoutes(false, true)
        })

        socket.on('updateOrders', () => {
            OrderActions.getRoutes(false, true)
        })

        socket.on('updateShifts', () => {
            OrderActions.getShifts()
        })

        socket.on('updateCustomers', () => {
            OrderActions.getCustomers()
        })

        this.watchPosition = geolocation.watchPosition((err, location) => {
            if (err) {
                return
            }

            OrderActions.setMessengerLocation(location)
        })

        // Also when the position doesn't change, so the ETA is still recalculated while the messenger is standing still
        this.locationInterval = setInterval(() => this.sendMessengerLocation(), LOCATION_UPDATE_INTERVAL_MS)

        // Timers hardly run while the app is in the background, so the position is sent as soon as it is opened again
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this.sendMessengerLocation()
            }
        })
    }

    onSetDate(date) {
        this.setState({date})
        OrderActions.getRoutes(true)
    }

    onGetCustomers() {
        socket.emit('customers.get', localStorage.mtoken, [], (err, customers) => {
            if (!err) {
                this.setState({customers})
            }
        })
    }

    async onGetShifts() {
        const date = time.today()

        try {
            const {shifts} = await emit('shifts.getMessengerShifts', date)

            const {selectedShift} = this.state
            const stillExists = shifts.find((s) => s._id === selectedShift)
            const shift = stillExists || shifts[0] || null

            localStorage.selectedShift = shift?._id || ''

            this.setState({
                shifts,
                selectedShift: shift?._id || '',
                shift
            })
        } catch (err) {
            this.setState({err: err.message})
        }
    }

    onSelectShift(selectedShift) {
        localStorage.selectedShift = selectedShift

        const {shifts} = this.state
        const shift = shifts.find((s) => s._id === selectedShift) || null

        this.setState({selectedShift, shift})
    }

    onGetRoutes(loading, showNotifications) {
        const {date} = this.state
        this.setState({loading: !!loading})

        socket.emit('orders.getMessengerRoutes', localStorage.mtoken, date, (err, routes) => {
            if (!err) {
                const {selectedRoute} = this.state

                // TODO rename routes to routeObject everywhere?
                const routeObject = {}
                _.sortBy(routes, (route) => {
                    if (route.completed) {
                        return '24:00'
                    }

                    if (route.plannedStartTime) {
                        return route.plannedStartTime
                    }

                    if (route.startTime) {
                        return moment(route.startTime).format('HH:mm')
                    }

                    return '24:00'
                }).map((route) => {
                    routeObject[route._id] = route
                })
                routes = routeObject

                const selectedRouteExists = _.keys(routes).includes(selectedRoute)
                const newSelectedRoute = selectedRouteExists ? selectedRoute : _.keys(routes)[0]

                localStorage.selectedRoute = newSelectedRoute

                this.setState({
                    routes,
                    selectedRoute: newSelectedRoute,
                    isScanning: selectedRouteExists ? this.state.isScanning : false,
                    route: selectedRouteExists ? this.state.route : {},
                    loading: false
                })

                // If newSelectedRoute is not undefined, get the route
                if (newSelectedRoute) {
                    OrderActions.getRoute(loading, showNotifications)
                }
            }
        })
    }

    onGetRoute(loading, showNotifications) {
        const {selectedRoute, readyColli} = this.state
        this.setState({loading: !!loading})

        const oldRoute = {
            _id: this.state.route?._id,
            addresses: {}
        }

        this.state.route?.addresses?.map((address) => {
            oldRoute.addresses[address.id] = address
        })

        socket.emit('orders.getMessengerRoute', localStorage.mtoken, selectedRoute, (err, route) => {
            if (!err) {
                route.hasUnstartedColli = false // Colli that are not started, including not ready to start
                route.hasReadyToStartColli = false // Colli that are not started and ready to start
                route.hasStartedColli = false // Colli that are started
                route.hasUnCompletedColli = false // Colli that are started but not yet completed
                route.hasCompletedColli = false

                // Ready colli count used for Scan & Start only
                route.hasNotReadyColli = false
                route.allColliCount = 0
                route.readyColliCount = 0
                route.openColliCount = 0

                route.addresses = []
                route.colli = []
                route.ids = []
                route.reversible = true

                // TODO rename orders to ordersObject everywhere?
                const ordersObject = {}

                route.orders.map((order) => {
                    ordersObject[order._id] = order
                })

                route.orders = ordersObject

                Object.values(route.orders).map((order) => {
                    route.ids.push(order._id)

                    const remainingAddresses = order.addresses.filter((address) => !address.hasArrived)

                    if (remainingAddresses.length > 1 && order.addressOrder !== 'random') {
                        route.reversible = false
                    }

                    order.colli.map((collo, index) => {
                        collo.index = index
                        collo.orderId = order._id
                        collo.pickupAddress = {...order.addresses[collo.pickupAddressIndex]}
                        collo.deliveryAddress = {...order.addresses[collo.deliveryAddressIndex]}

                        collo.expected = collo.status === 'expected'

                        if (collo.status === 'pickup' || (typeof collo.deliveryAddressIndex === 'number' && ['expected', 'athub'].includes(collo.status))) {
                            collo.started = false
                            route.hasUnstartedColli = true

                            if (!collo.expected) {
                                route.hasReadyToStartColli = true
                            }
                        } else {
                            collo.started = true
                            route.hasStartedColli = true
                        }

                        collo.ready = collo.started || collo.loaded || ['pickup', 'outforpickup', 'outfordelivery'].includes(collo.status) || readyColli[collo.orderId]?.[collo.index]
                        collo.loaded = collo.ready // TODO use either ready or loaded for now loaded is necessary because the backend checks for collo.loaded

                        if (!collo.ready) {
                            route.hasNotReadyColli = true
                        }

                        collo.routeIndex = route.colli.length
                        collo.routeStartTime = order.routeStartTime
                        route.colli.push(collo)

                        if (['notcollected', 'collected', 'notsupplied', 'notdelivered', 'delivered', 'refused', 'missing', 'manco', 'cancelled'].includes(collo.status)) {
                            route.hasCompletedColli = true
                            return
                        }

                        if (!collo.started) {
                            route.allColliCount += 1

                            if (collo.ready) {
                                route.readyColliCount += 1
                            } else {
                                route.openColliCount += 1
                            }
                        } else {
                            route.hasUnCompletedColli = true
                        }
                    })

                    order.addresses.map((address, index) => {
                        // Completed addresses are shown from route.completedAddresses (a snapshot taken when the
                        // stop was completed on this route), not from the order's own current state - once an
                        // order is rescheduled, its live addresses no longer reflect what happened on this route.
                        if (address.hasArrived) {
                            return
                        }

                        address.id = `${order._id} ${index}`
                        address.index = index
                        address.orderId = order._id
                        address.customerId = order.customer
                        address.senderAddress = {...order.senderAddress}
                        address.customerAddress = {...order.customerAddress}
                        address.history = [...order.history]
                        address.previousId = order.previousId
                        address.status = order.status
                        // The retour address of a retour, originalAddress is the (first) address it is returned from
                        address.isRetour = isRetourOrder(order)
                        address.originalAddress = address.isRetour ? order.originalAddresses?.[0] : undefined

                        if (order.status === dbCodes.status.verwacht()) {
                            address.isExpected = true
                        }

                        address.isStarted = order.status === dbCodes.status.onderweg()

                        // Set next stop if priority is not set, because it can always be the next stop
                        if (typeof address.priority !== 'number') {
                            address.isNextStop = true
                        }

                        address.colli = order.colli.filter((collo) => {
                            if (address.type === 'pickup' && collo.pickupAddressIndex === address.index) {
                                if (!address.isStarted) {
                                    return ['pickup'].includes(collo.status)
                                } else {
                                    const outforpickup = ['outforpickup'].includes(collo.status)

                                    if (outforpickup) {
                                        address.isReady = true
                                    }

                                    return ['outforpickup'].includes(collo.status)
                                }
                            }

                            if (address.type === 'delivery' && collo.deliveryAddressIndex === address.index) {
                                if (!address.isStarted) {
                                    return ['expected', 'pickup', 'athub'].includes(collo.status)
                                } else {
                                    const outfordelivery = ['outfordelivery'].includes(collo.status)

                                    if (outfordelivery) {
                                        address.isReady = true
                                    }

                                    return ['outforpickup', 'outfordelivery'].includes(collo.status)
                                }
                            }
                        })

                        if (!address.isStarted || order.date > moment().format('YYYY-MM-DD')) {
                            address.isReady = false
                        }

                        address.hasUnstartedColli = _.some(address.colli, (collo) => !collo.started)

                        route.addresses.push(address)
                    })
                })

                // Stops at the same address share a priority, routeSequence keeps the order they were put in
                route.addresses = _.sortBy(route.addresses, [(address) => address.priority, (address) => address.routeSequence])

                const nextStopAddress = route.addresses.find((address) => address.isReady)

                if (nextStopAddress) {
                    nextStopAddress.isNextStop = true
                }

                // Handling one of these addresses now would skip a stop in the fixed zone, or handle one before its turn
                route.addresses.map((address) => {
                    address.skipsFixedZone = !address.isNextStop && (!!address.anchor || !!nextStopAddress?.anchor)
                })

                route.todoAddresses = route.addresses

                // No actions are possible on a completed address, so it's rendered straight from the immutable
                // snapshot the backend took when the stop was completed - no need for (and no live order to read
                // for) a rescheduled order's current state. The snapshot is kept in the order the stops were completed.
                route.completedAddresses = (route.completedAddresses || []).map((address) => {
                    // LEGACY: remove once no messenger opens a route from before #3671 anymore, together with legacyCompletedColli.
                    // Snapshots taken before colli and status were stored on them fall back to the order, if it's still on this route
                    const order = !address.colli ? route.orders[address.orderId] : undefined

                    return {
                        ...address,
                        id: `${address.orderId} ${address.index}`,
                        colli: address.colli || (order ? legacyCompletedColli(order, address) : []),
                        status: address.status ?? order?.status,
                        history: address.history || []
                    }
                })

                // Open addresses by priority, then completed addresses in completion order
                route.addresses = [...route.todoAddresses, ...route.completedAddresses]

                // Consecutive open stops at the same address are shown and handled as one bundle
                route.todoItems = stopBundles.getStopBundles(route.todoAddresses, nextStopAddress)

                let routeUpdatedNotifcation = {...this.state.routeUpdatedNotifcation}

                if (showNotifications && route.started) {
                    if (oldRoute._id === route._id) {
                        route.addresses.map((address, index) => {
                            if (!oldRoute.addresses[address.id]) {
                                routeUpdatedNotifcation.added.push(address.stopNumber || address.priority || index + 1)
                            }
                        })

                        Object.values(oldRoute.addresses).map((address, index) => {
                            if (!route.addresses.find((newAddress) => newAddress.id === address.id)) {
                                routeUpdatedNotifcation.removed.push(address.stopNumber || address.priority || index + 1)
                            }
                        })

                        routeUpdatedNotifcation.addedMessage = ''

                        if (routeUpdatedNotifcation.added.length > 0) {
                            routeUpdatedNotifcation.addedMessage = `${routeUpdatedNotifcation.added.length === 1 ? 'Stop' : 'Stops'} ${routeUpdatedNotifcation.added.join(', ')} toegevoegd.\n`
                        }

                        if (routeUpdatedNotifcation.removed.length > 0) {
                            routeUpdatedNotifcation.removedMessage = `${routeUpdatedNotifcation.removed.length === 1 ? 'Stop' : 'Stops'}  ${routeUpdatedNotifcation.removed.join(', ')} verwijderd.\n`
                        }
                    } else {
                        routeUpdatedNotifcation = {added: [], removed: [], addedMessage: '', removedMessage: ''}
                    }
                }

                this.setState({
                    route,
                    routeUpdatedNotifcation,
                    loading: false
                })

                // The first position usually comes in before the route is loaded, and a messenger standing still gets
                // no new one, so send it now instead of a minute later. Still at most once a minute.
                this.sendMessengerLocation()
            }
        })
    }

    onSelectRoute(selectedRoute) {
        localStorage.selectedRoute = selectedRoute
        this.setState({selectedRoute, routeUpdatedNotifcation: {added: [], removed: [], addedMessage: '', removedMessage: ''}})
        OrderActions.getRoute(true)
    }

    onSetRouteStartTime(routeStartTime) {
        const {route} = this.state

        const ids = _.uniq(route.addresses.map((address) => address.orderId))

        socket.emit('orders.setRouteStartTime', localStorage.mtoken, ids, routeStartTime)
    }

    onStartScanning() {
        const route = {...this.state.route}

        this.setState({route, isScanning: true})
    }

    onCancelScanning() {
        const route = {...this.state.route}
        const readyColli = {...this.state.readyColli}

        // Reset colli in route.colli
        route.colli.forEach((collo) => {
            if (readyColli[collo.orderId] && readyColli[collo.orderId][collo.index]) {
                collo.ready = false
            }
        })

        this.setState({route, isScanning: false, readyColli: {}})
    }

    onSetColloReady(collo, ready, callback) {
        const route = {...this.state.route}
        const readyColli = {...this.state.readyColli}

        collo = route.colli[collo.routeIndex]

        if (!collo) {
            callback('Geen collo gevonden.')
            return
        }

        collo.ready = ready
        collo.loaded = ready // TODO pick between loaded and ready

        // Store status in readyColli, to prevent losing status in route updates
        readyColli[collo.orderId] = readyColli[collo.orderId] || {}
        readyColli[collo.orderId][collo.index] = collo.ready

        route.allColliCount = 0
        route.readyColliCount = 0
        route.openColliCount = 0

        route.colli.map((collo) => {
            if (['delivered', 'collected', 'notsupplied'].includes(collo.status)) {
                return
            }

            if (!collo.started) {
                route.allColliCount += 1

                if (collo.ready) {
                    route.readyColliCount += 1
                } else {
                    route.openColliCount += 1
                }
            }
        })

        this.setState({route, readyColli})
        callback()
    }

    onLoadRoute(...args) {
        this.setState({isScanning: false, readyColli: {}})

        socket.emit('orders.loadRoute', localStorage.mtoken, ...args)
    }

    onStartRoute(...args) {
        this.setState({isScanning: false})

        socket.emit('orders.startRoute', localStorage.mtoken, ...args)
    }

    onStopRoute(...args) {
        socket.emit('orders.stopRoute', localStorage.mtoken, ...args)
    }

    onPlanRoute(routeId, optimize, reverse, changedAddresses, callback) {
        const route = {...this.state.route}

        if (changedAddresses) {
            // Completed addresses are included in changedAddresses because they are rendered in the same list, so we need to filter them out
            route.addresses = changedAddresses.filter((address) => !address.hasArrived)
        }

        this.setState({route, routeLoading: true})

        socket.emit('orders.planRoute', localStorage.mtoken, {routeId, optimize, reverse, changedAddresses}, (err) => {
            this.setState({routeLoading: false, error: err})

            typeof callback === 'function' && callback(err)
        })
    }

    onSetRouteReady(routeId, ready, callback) {
        this.setState({routeLoading: true})

        socket.emit('orders.setRouteReady', localStorage.mtoken, routeId, ready, (err) => {
            this.setState({routeLoading: false, error: err})

            typeof callback === 'function' && callback(err)
        })
    }

    onSetRouteLocked(routeId, locked, callback) {
        this.setState({routeLoading: true})

        socket.emit('orders.setRouteLocked', localStorage.mtoken, routeId, locked, (err) => {
            this.setState({routeLoading: false, error: err})

            typeof callback === 'function' && callback(err)
        })
    }

    onSetAddressLocked(...args) {
        socket.emit('orders.setAddressLocked', localStorage.mtoken, ...args)
    }

    onUpdateRoute(update, callback) {
        const route = {...this.state.route}

        this.setState({route: {...route, ...update, routeLoading: true}})

        socket.emit('orders.updateRoute', localStorage.mtoken, route._id, update, (err) => {
            this.setState({routeLoading: false, error: err})

            typeof callback === 'function' && callback(err)
        })
    }

    onScan() {
        // const {route, scannedColli} = this.state

        // socket.emit('orders.scan', localStorage.mtoken, ...args)
    }

    onDownloadAttachment(fileId, callback) {
        socket.emit('addresses.downloadAttachment', localStorage.mtoken, fileId, callback)
    }

    onReadNotification(type) {
        const routeUpdatedNotifcation = {...this.state.routeUpdatedNotifcation}

        if (type === 'added') {
            routeUpdatedNotifcation.added = []
            routeUpdatedNotifcation.addedMessage = ''
        }

        if (type === 'removed') {
            routeUpdatedNotifcation.removed = []
            routeUpdatedNotifcation.removedMessage = ''
        }

        this.setState({routeUpdatedNotifcation})
    }

    onSetMessengerLocation(location) {
        this.setState({location})

        this.sendMessengerLocation()
    }

    // Passes the last known position on to the planning once a minute while the route is underway, the server also
    // recalculates the ETA from it now and then. A position that comes in sooner is sent when the minute has passed, so
    // the planning always ends up with the latest one.
    sendMessengerLocation() {
        const elapsed = Date.now() - (this.lastLocationSentAt || 0)

        if (elapsed < LOCATION_UPDATE_INTERVAL_MS) {
            this.locationTimeout = this.locationTimeout || setTimeout(() => {
                this.locationTimeout = null
                this.sendMessengerLocation()
            }, LOCATION_UPDATE_INTERVAL_MS - elapsed)

            return
        }

        const {route, location} = this.state

        if (!route?._id || !route.started || route.completed || !location?.lat) {
            return
        }

        this.lastLocationSentAt = Date.now()

        emit('orders.updateMessengerLocation', route._id, {lat: location.lat, lng: location.lng}).catch((err) => console.error(err))
    }

    onGetNotPossibleReasons(...args) {
        socket.emit('orders.getNotPossibleReasons', localStorage.mtoken, ...args)
    }

    onGetAddressPackages(type, packagingIds, address, callback) {
        socket.emit('packages.getAddressPackages', localStorage.mtoken, type, packagingIds, address, callback)
    }
}
